import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';

const Hero = () => {
  const { t } = useTranslation();
  const [textVisible, setTextVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Estados del reproductor de audio
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [audioReady, setAudioReady] = useState(false); // Nuevo estado para indicar precarga
  const audioRef = useRef(null);

  useEffect(() => {
    const timer1 = setTimeout(() => setTextVisible(true), 500);
    const timer2 = setTimeout(() => setCtaVisible(true), 1500);
    
    // Video will autoplay automatically with HTML5 video element
    
    // Parallax effect
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      setParallaxY(scrolled * 0.5);
    };

    // Mouse movement effect
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX - window.innerWidth / 2) * 0.01,
        y: (e.clientY - window.innerHeight / 2) * 0.01,
      });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Funciones del reproductor de audio - OPTIMIZADO PARA REPRODUCCIÓN INSTANTÁNEA
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    
    // Marcar audio como listo cuando se puede reproducir
    const handleCanPlayThrough = () => {
      setAudioReady(true);
      console.log('🎵 Hero audio READY for INSTANT play');
    };
    
    // Marcar como listo incluso con menos buffer
    const handleCanPlay = () => {
      setAudioReady(true);
      console.log('🎵 Hero audio can play now');
    };
    
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', () => setIsPlaying(false));
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('canplay', handleCanPlay);
    
    // PRECARGA ULTRA-AGRESIVA - Cargar inmediatamente
    audio.preload = 'auto';
    audio.volume = volume;
    audio.load();
    
    // Forzar descarga completa del audio
    const forcePreload = () => {
      if (audio.buffered.length > 0) {
        console.log('🎵 Audio buffer loaded:', audio.buffered.end(0), '/', audio.duration);
        if (audio.buffered.end(0) >= audio.duration * 0.5) {
          setAudioReady(true);
          console.log('🎵 Audio PRELOADED and READY for instant play!');
        }
      }
    };
    
    audio.addEventListener('progress', forcePreload);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', () => setIsPlaying(false));
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('progress', forcePreload);
    };
  }, []);

  // Function to register play in backend statistics
  const registerPlay = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      await fetch(`${API_URL}/api/tracks/0/play`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      console.log('📊 Play registered in statistics for Afternoon with Myself');
    } catch (error) {
      console.error('Failed to register play:', error);
    }
  };

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) {
      console.log('No audio element found');
      return;
    }

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        console.log('🎵 PLAYING HERO AUDIO - Afternoon with Myself');
        
        // Register play in backend statistics FIRST
        registerPlay();
        
        // Reproducción inmediata
        setIsPlaying(true);
        
        // Intentar reproducir inmediatamente
        try {
          await audio.play();
          console.log('🎵 Afternoon with Myself playing instantly!');
        } catch (playError) {
          console.log('🎵 Instant play failed, loading audio...');
          setIsPlaying(false);
          
          // Si falla, mostrar que está cargando
          setTimeout(async () => {
            try {
              await audio.play();
              setIsPlaying(true);
              console.log('🎵 Afternoon with Myself playing after buffer');
            } catch (retryError) {
              console.error('🎵 Afternoon with Myself audio failed:', retryError);
              alert('Loading Afternoon with Myself... please wait a moment and try again');
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Audio play/pause error:', error);
      setIsPlaying(false);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value;
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const scrollToMusic = () => {
    const element = document.getElementById('music');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Fondo del artista con sintetizador */}
      {/* Imagen de fondo principal */}
      <div 
        className="absolute inset-0 w-full h-full bg-no-repeat"
        style={{
          backgroundImage: 'url(https://0xx2mevl0on6cu8y.public.blob.vercel-storage.com/Portada%20Cover%20Take%20My%20Hand%20Header%20Spotify-2.jpg)',
          backgroundSize: '100% auto',
          backgroundPosition: 'center center',
          zIndex: 1,
          transform: `scale(1.05) translateY(${parallaxY * 0.2}px)`,
        }}
      />
      
      {/* Overlay oscuro para contraste - ajustado para tonos dorados */}
      <div className="absolute inset-0 bg-black/15 z-10"></div>
      
      {/* Animated light rays - ajustados para ambiente dorado */}
      <div className="absolute inset-0 opacity-15 z-20">
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-amber-300/40 to-transparent animate-light-ray-1"></div>
        <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-orange-200/30 to-transparent animate-light-ray-2"></div>
        <div className="absolute top-0 left-2/3 w-px h-full bg-gradient-to-b from-transparent via-yellow-200/35 to-transparent animate-light-ray-3"></div>
      </div>
      
      {/* Floating sand particles effect */}
      <div className="absolute inset-0 opacity-40 z-20">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className={`particle absolute w-1 h-1 bg-gradient-to-r from-blue-300 to-white rounded-full animate-sand-fall-${(i % 4) + 1}`}
            style={{
              left: `${10 + (i * 7)}%`,
              top: `${20 + (i * 5)}%`,
              animationDelay: `${i * 0.8}s`,
            }}
          />
        ))}
      </div>
      
      {/* Dynamic gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-blue-900/5 to-black/30 animate-gradient-shift z-15"></div>
      
      {/* Breathing atmospheric effect */}
      <div className="absolute inset-0 bg-gradient-radial from-blue-400/15 via-transparent to-transparent animate-atmosphere-pulse z-15"></div>
      
      {/* Content */}
      <div className="relative z-30 px-6 md:px-16 lg:px-24 max-h-[90vh] flex flex-col justify-center">
        <div className="w-full md:max-w-[55%]">
        <div className={`text-center md:text-left transition-all duration-1000 ${
          textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h1 className="text-4xl md:text-7xl font-extralight tracking-[0.15em] md:tracking-[0.2em] mb-2 animate-text-shimmer">
            <span className="text-white drop-shadow-2xl">{t('hero.title')}</span>
            <br />
            <div className="flex justify-center md:justify-start items-center mt-2">
              <img 
                src="https://lh3.googleusercontent.com/d/14mciIfGoUwM507x8Zer37U-lJHypp3OR" 
                alt="SYNTETIK Logo" 
                className="h-16 md:h-24 object-contain animate-gradient-text drop-shadow-2xl"
                onError={(e) => {
                  // Fallback to text if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline';
                }}
              />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-white to-blue-100 animate-gradient-text hidden">
                {t('hero.subtitle')}
              </span>
            </div>
          </h1>
          
          <p className="text-base md:text-xl font-light text-blue-100 mb-6 max-w-lg leading-relaxed animate-text-float drop-shadow-lg">
            {t('hero.tagline')}
          </p>
        </div>
        
        <div className={`transition-all duration-1000 delay-500 ${
          ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <Button
            onClick={scrollToMusic}
            className="bg-transparent border border-white/30 text-white hover:bg-white hover:text-black hover:scale-105 transform transition-all duration-300 px-8 md:px-12 py-3 md:py-4 text-sm md:text-lg font-light tracking-widest rounded-none backdrop-blur-sm animate-button-glow"
          >
            {t('hero.cta')}
          </Button>
          
          {/* Web Audio Player - Ultra Modern */}
          <div className="mt-4 flex justify-center md:justify-start" data-testid="hero-audio-player">
            <audio ref={audioRef} preload="auto" crossOrigin="anonymous">
              <source src="https://0xx2mevl0on6cu8y.public.blob.vercel-storage.com/Take%20My%20Hand%20Master%20Final.mp3" type="audio/mpeg" />
            </audio>

            <div className="flex flex-col items-center gap-4 w-full max-w-xs">
              {/* Play button + track info */}
              <div className="flex items-center gap-4">
                <button
                  onClick={togglePlay}
                  data-testid="hero-play-button"
                  className="group w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-all duration-500 border border-white/25 hover:border-white/60 hover:scale-110 relative"
                >
                  {/* Glow ring on hover */}
                  <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[0_0_20px_rgba(255,255,255,0.15)]"></div>
                  {isPlaying ? (
                    <div className="flex gap-[4px]">
                      <div className="w-[2px] h-[14px] bg-white rounded-full"></div>
                      <div className="w-[2px] h-[14px] bg-white rounded-full"></div>
                    </div>
                  ) : (
                    <div className="w-0 h-0 border-l-[10px] border-l-white border-y-[6px] border-y-transparent ml-1"></div>
                  )}
                </button>

                <div>
                  <div className="text-white text-sm tracking-[0.08em] font-light">
                    Take My Hand
                  </div>
                  <div className="text-white/30 text-[11px] tracking-[0.2em] font-light">
                    SYNTETIK
                  </div>
                </div>
              </div>

              {/* Progress bar - ultra thin */}
              <div className="w-full flex items-center gap-3">
                <span className="text-[9px] text-white/25 tabular-nums tracking-wider" data-testid="hero-current-time">
                  {formatTime(currentTime)}
                </span>
                <div
                  className="flex-1 h-[1.5px] bg-white/10 cursor-pointer group relative"
                  onClick={handleProgressClick}
                  data-testid="hero-progress-bar"
                >
                  <div
                    className="h-full bg-white/60 transition-all duration-150 relative"
                    style={{ width: `${progressPercent}%` }}
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[5px] h-[5px] bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
                <span className="text-[9px] text-white/25 tabular-nums tracking-wider" data-testid="hero-duration">
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
      
      {/* Animated Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-scroll-indicator z-30">
        <div className="w-px h-12 bg-gradient-to-b from-white/60 to-transparent animate-pulse-vertical"></div>
        <div className="mt-2 text-xs text-white/60 font-light tracking-widest animate-fade-pulse">{t('hero.scroll')}</div>
      </div>
    </section>
  );
};

export default Hero;